import { useState } from 'react'
import type { CdphReportListItem, CdphReportDetail, CdphReportStatus } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Alert,
  StatusBadge,
  ConfirmDialog,
  Modal,
  btnSecondary,
  colors,
  input,
} from '../../components/ui'
import type { Column } from '../../components/ui'

// §7.6.23 Slice 4 -- CDPH report queue. Server-side status filter + pagination
// via `useApiList` over `GET /admin/cdph/reports`; row click opens the detail
// modal (`GET /admin/cdph/reports/:id`, which is the only endpoint that
// returns the stored HL7 message -- the list never does, PHI posture).

const STATUS_FILTERS: readonly (CdphReportStatus | 'all')[] = [
  'all',
  'pending',
  'sent',
  'accepted',
  'rejected',
  'error',
  'exhausted',
]

const STATUS_LABELS: Record<CdphReportStatus | 'all', string> = {
  all: 'All',
  pending: 'Pending',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  error: 'Error',
  exhausted: 'Exhausted',
}

function formatTs(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '--'
}

function truncate(text: string | null, max = 60): string {
  if (!text) return '--'
  return text.length > max ? `${text.slice(0, max)}...` : text
}

export function CdphReportsPage() {
  const [statusFilter, setStatusFilter] = useState<CdphReportStatus | 'all'>('all')
  const { data, total, page, loading, error, setPage, refresh } = useApiList<CdphReportListItem>({
    path: '/admin/cdph/reports',
    limit: 25,
    filters: { status: statusFilter === 'all' ? undefined : statusFilter },
  })

  const [detailId, setDetailId] = useState<string | null>(null)
  const [detail, setDetail] = useState<CdphReportDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const resendModal = useModal()
  const [resendTarget, setResendTarget] = useState<CdphReportListItem | null>(null)
  const [resending, setResending] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)

  function openDetail(row: CdphReportListItem) {
    setDetailId(row.id)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(true)
    api
      .get<CdphReportDetail>(`/admin/cdph/reports/${row.id}`)
      .then((res) => {
        if (res.data) setDetail(res.data)
      })
      .catch((err) => {
        setDetailError(err instanceof ApiError ? err.message : 'Failed to load report detail')
      })
      .finally(() => setDetailLoading(false))
  }

  function closeDetail() {
    setDetailId(null)
    setDetail(null)
    setDetailError(null)
  }

  function openResend(row: CdphReportListItem) {
    setResendTarget(row)
    setResendError(null)
    resendModal.open()
  }

  async function confirmResend() {
    if (!resendTarget) return
    setResending(true)
    setResendError(null)
    try {
      await api.post(`/admin/cdph/reports/${resendTarget.id}/resend`, {})
      resendModal.close()
      setResendTarget(null)
      refresh()
    } catch (err) {
      setResendError(err instanceof ApiError ? err.message : 'Resend failed')
    } finally {
      setResending(false)
    }
  }

  const columns: readonly Column<CdphReportListItem>[] = [
    { key: 'barcode', header: 'Barcode', render: (r) => r.barcode ?? '--' },
    { key: 'testType', header: 'Test', render: (r) => r.testType ?? '--' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'attempts', header: 'Attempts', render: (r) => r.attempts },
    {
      key: 'ackText',
      header: 'Ack Text',
      render: (r) => (
        <span title={r.ackText ?? undefined} style={{ fontSize: '0.8125rem' }}>
          {truncate(r.ackText)}
        </span>
      ),
    },
    { key: 'sentAt', header: 'Sent', render: (r) => formatTs(r.sentAt) },
    { key: 'createdAt', header: 'Created', render: (r) => formatTs(r.createdAt) },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <button
          type="button"
          style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
          onClick={(e) => {
            e.stopPropagation()
            openResend(r)
          }}
          data-testid={`resend-${r.id}`}
        >
          Resend
        </button>
      ),
    },
  ]

  const totalPages = Math.max(1, Math.ceil(total / 25))

  return (
    <div>
      <PageHeader
        title="CDPH Report Queue"
        subtitle={`${total} total submissions`}
        actions={
          <select
            style={{ ...input, width: 'auto' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CdphReportStatus | 'all')}
            data-testid="cdph-report-status-filter"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        }
      />

      {error && <Alert type="error" message={error} />}

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={data as CdphReportListItem[]}
          keyField="id"
          onRowClick={openDetail}
          emptyMessage="No CDPH reports found."
        />
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
          <button type="button" style={btnSecondary} disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
            Page {page} of {totalPages} ({total} total)
          </span>
          <button type="button" style={btnSecondary} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}

      {detailId && (
        <Modal isOpen onClose={closeDetail} title="CDPH Report Detail" width="640px">
          {detailLoading ? (
            <div style={{ color: colors.textMuted }}>Loading...</div>
          ) : detailError ? (
            <Alert type="error" message={detailError} />
          ) : detail ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
              <div>
                <strong>{detail.barcode ?? '--'}</strong> &middot; {detail.testType ?? '--'} &middot;{' '}
                <StatusBadge status={detail.status} />
              </div>
              <div style={{ color: colors.textMuted }}>
                Attempts: {detail.attempts} &middot; Sent: {formatTs(detail.sentAt)} &middot; Next attempt:{' '}
                {formatTs(detail.nextAttemptAt)}
              </div>
              {detail.ackCode && (
                <div>
                  <strong>ACK:</strong> {detail.ackCode} {detail.ackText ? `-- ${detail.ackText}` : ''}
                </div>
              )}
              {detail.errDetails && detail.errDetails.length > 0 && (
                <div>
                  <strong>Errors:</strong>
                  <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                    {detail.errDetails.map((e, i) => (
                      <li key={i}>{[e.location, e.severity, e.reason].filter(Boolean).join(' -- ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <strong>HL7 Message</strong>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    background: colors.bgMuted,
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: `1px solid ${colors.border}`,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    maxHeight: '260px',
                    overflow: 'auto',
                  }}
                >
                  {detail.hl7Message ?? '(not yet submitted)'}
                </pre>
              </div>
            </div>
          ) : null}
        </Modal>
      )}

      <ConfirmDialog
        isOpen={resendModal.isOpen}
        onClose={() => {
          resendModal.close()
          setResendTarget(null)
        }}
        onConfirm={confirmResend}
        title="Resend CDPH Report"
        message={`Resend this report to CDPH SaPHIRE? It will be reset to pending and resubmitted on the next submission cycle.${resendError ? `\n\n${resendError}` : ''}`}
        confirmLabel="Resend"
        variant="primary"
        loading={resending}
      />
    </div>
  )
}
